import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3, Table, Play, Plus, Trash2, Edit2, Save, Clock,
  Mail, Download, Filter, X, BarChart2,
  PieChart, TrendingUp, LayoutGrid,
} from "lucide-react";
import { apiClient } from "../services/api";
import i18n from "../i18n";

const TABS = [
  { id: "reports", icon: LayoutGrid },
  { id: "builder", icon: BarChart3 },
  { id: "schedules", icon: Clock },
];

const CHART_TYPES = [
  { id: "table", icon: Table },
  { id: "bar", icon: BarChart2 },
  { id: "line", icon: TrendingUp },
  { id: "pie", icon: PieChart },
];

const FREQUENCIES = ["daily", "weekly", "monthly"];

const chartTypeLabel = (type, t) => t(`reportBuilderHub.chartTypes.${type}`, { defaultValue: type });
const frequencyLabel = (frequency, t) => t(`reportBuilderHub.frequencies.${frequency}`, { defaultValue: frequency });
const sourceLabel = (source, t) => t(`reportBuilderHub.sources.${source}`, { defaultValue: source });

export default function ReportBuilderHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "reports";
  const setTab = (id) => setSearchParams({ tab: id });

  const [editingReport, setEditingReport] = useState(null);

  const openBuilder = (report = null) => {
    setEditingReport(report);
    setTab("builder");
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <BarChart3 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("reportBuilderHub.title")}</h1>
            <p className="text-muted-foreground text-sm">
              {t("reportBuilderHub.subtitle")}
            </p>
          </div>
          <button
            onClick={() => openBuilder()}
            className="ml-auto flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> {t("reportBuilderHub.newReport")}
          </button>
        </div>

        <div className="flex gap-1 border-b">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t(`reportBuilderHub.tabs.${tab.id}`)}
              </button>
            );
          })}
        </div>

        {activeTab === "reports" && <ReportsTab onEdit={openBuilder} />}
        {activeTab === "builder" && (
          <BuilderTab
            initialReport={editingReport}
            onSaved={() => { setEditingReport(null); setTab("reports"); }}
          />
        )}
        {activeTab === "schedules" && <SchedulesTab />}
      </div>
    </div>
  );
}

/* ── Reports list ───────────────────────────────────────────────────────────── */
function ReportsTab({ onEdit }) {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);

  const load = () =>
    apiClient.get("v1/report-builder/reports").then((r) => setReports(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const handleRun = async (id) => {
    setRunning(id);
    setResult(null);
    try {
      const r = await apiClient.get(`v1/report-builder/reports/${id}/run`);
      setResult({ id, data: r.data });
    } finally {
      setRunning(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("reportBuilderHub.reports.deleteConfirm"))) return;
    await apiClient.delete(`v1/report-builder/reports/${id}`);
    load();
    if (result?.id === id) setResult(null);
  };

  if (reports.length === 0) {
    return (
      <div className="text-center py-16 border rounded-xl bg-card">
        <BarChart3 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <h3 className="font-semibold mb-1">{t("reportBuilderHub.reports.emptyTitle")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("reportBuilderHub.reports.emptySubtitle")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3">
        {reports.map((r) => (
          <ReportCard
            key={r.id}
            report={r}
            isRunning={running === r.id}
            onRun={() => handleRun(r.id)}
            onEdit={() => onEdit(r)}
            onDelete={() => handleDelete(r.id)}
          />
        ))}
      </div>

      {result && (
        <ResultPanel
          data={result.data}
          onClose={() => setResult(null)}
        />
      )}
    </div>
  );
}

function ReportCard({ report, isRunning, onRun, onEdit, onDelete }) {
  const { t } = useTranslation();

  return (
    <div className="bg-card border rounded-xl p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 bg-primary/10 rounded-lg shrink-0">
          <BarChart3 className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{report.name}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
              {sourceLabel(report.data_source, t)}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
              {chartTypeLabel(report.chart_type, t)}
            </span>
            {report.is_public && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 shrink-0">{t("reportBuilderHub.common.public")}</span>
            )}
          </div>
          {report.description && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{report.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onRun}
          disabled={isRunning}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          {isRunning ? t("reportBuilderHub.reports.running") : t("reportBuilderHub.reports.run")}
        </button>
        <button onClick={onEdit} className="p-1.5 hover:bg-accent rounded">
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        <button onClick={onDelete} className="p-1.5 hover:bg-accent rounded text-red-500">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── Result panel ───────────────────────────────────────────────────────────── */
function ResultPanel({ data, onClose }) {
  const { t } = useTranslation();
  const exportCsv = () => {
    if (!data.rows?.length) return;
    const headers = data.columns.join(",");
    const rows = data.rows.map((r) => data.columns.map((c) => `"${r[c] ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([headers + "\n" + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rapport.csv";
    a.click();
  };

  return (
    <div className="bg-card border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/40">
        <span className="font-medium text-sm">
          {t("reportBuilderHub.result.title", { count: data.total, suffix: data.total !== 1 ? "s" : "" })}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            className="flex items-center gap-1 text-xs px-3 py-1.5 border rounded-lg hover:bg-accent"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {data.rows?.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">{t("reportBuilderHub.result.empty")}</div>
      ) : (
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                {data.columns.map((col) => (
                  <th key={col} className="px-4 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i} className="border-b hover:bg-accent/30">
                  {data.columns.map((col) => (
                    <td key={col} className="px-4 py-2.5 whitespace-nowrap">
                      {row[col] !== null && row[col] !== undefined ? String(row[col]) : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Builder ────────────────────────────────────────────────────────────────── */
function BuilderTab({ initialReport, onSaved }) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState({});
  const [source, setSource] = useState(initialReport?.data_source || "");
  const [selectedCols, setSelectedCols] = useState(initialReport?.columns || []);
  const [filters, setFilters] = useState(initialReport?.filters || []);
  const [chartType, setChartType] = useState(initialReport?.chart_type || "table");
  const [groupBy, setGroupBy] = useState(initialReport?.group_by || "");
  const [rowLimit, setRowLimit] = useState(initialReport?.row_limit || 100);
  const [reportName, setReportName] = useState(initialReport?.name || "");
  const [isPublic, setIsPublic] = useState(initialReport?.is_public || false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("v1/report-builder/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const sourceInfo = catalog[source] || {};
  const availableCols = Object.entries(sourceInfo.columns || {});

  const toggleCol = (key) =>
    setSelectedCols((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );

  const addFilter = () =>
    setFilters((prev) => [...prev, { field: Object.keys(sourceInfo.filters || {})[0] || "", operator: "eq", value: "" }]);

  const removeFilter = (i) => setFilters((prev) => prev.filter((_, idx) => idx !== i));

  const updateFilter = (i, key, val) =>
    setFilters((prev) => prev.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));

  const handlePreview = async () => {
    if (!source) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const r = await apiClient.post("v1/report-builder/preview", {
        data_source: source,
        columns: selectedCols,
        filters,
        chart_type: chartType,
        group_by: groupBy || null,
        row_limit: Math.min(rowLimit, 50),
      });
      setPreview(r.data);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!reportName || !source) return;
    setSaving(true);
    try {
      const payload = {
        name: reportName,
        data_source: source,
        columns: selectedCols,
        filters,
        chart_type: chartType,
        group_by: groupBy || null,
        row_limit: rowLimit,
        is_public: isPublic,
      };
      if (initialReport) {
        await apiClient.put(`v1/report-builder/reports/${initialReport.id}`, payload);
      } else {
        await apiClient.post("v1/report-builder/reports", payload);
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Left panel — configuration */}
      <div className="lg:col-span-1 space-y-5">
        {/* Report name */}
        <div className="bg-card border rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-sm">{t("reportBuilderHub.builder.report")}</h3>
          <input
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            placeholder={t("reportBuilderHub.builder.namePlaceholder")}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
          />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="rounded"
            />
            {t("reportBuilderHub.builder.publicLabel")}
          </label>
        </div>

        {/* Data source */}
        <div className="bg-card border rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-sm">{t("reportBuilderHub.builder.dataSource")}</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(catalog).map(([key, info]) => (
              <button
                key={key}
                onClick={() => { setSource(key); setSelectedCols([]); setFilters([]); setGroupBy(""); }}
                className={`text-left px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  source === key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "hover:bg-accent"
                }`}
              >
                <div>{info.label}</div>
                <div className={`font-normal mt-0.5 ${source === key ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {info.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {source && (
          <>
            {/* Columns */}
            <div className="bg-card border rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">{t("reportBuilderHub.builder.columns")}</h3>
                <button
                  onClick={() =>
                    selectedCols.length === availableCols.length
                      ? setSelectedCols([])
                      : setSelectedCols(availableCols.map(([k]) => k))
                  }
                  className="text-xs text-primary hover:underline"
                >
                  {selectedCols.length === availableCols.length ? t("reportBuilderHub.builder.deselectAll") : t("reportBuilderHub.builder.selectAll")}
                </button>
              </div>
              <div className="space-y-1.5">
                {availableCols.map(([key, col]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedCols.includes(key)}
                      onChange={() => toggleCol(key)}
                      className="rounded"
                    />
                    {col.label}
                    <span className="text-xs text-muted-foreground">({col.type})</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="bg-card border rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-1.5">
                  <Filter className="h-3.5 w-3.5" /> {t("reportBuilderHub.builder.filters")}
                </h3>
                <button onClick={addFilter} className="text-xs text-primary hover:underline flex items-center gap-1">
                  <Plus className="h-3 w-3" /> {t("reportBuilderHub.common.add")}
                </button>
              </div>
              {filters.map((f, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select
                    value={f.field}
                    onChange={(e) => updateFilter(i, "field", e.target.value)}
                    className="flex-1 border rounded px-2 py-1 text-xs bg-background"
                  >
                    {Object.entries(sourceInfo.filters || {}).map(([k, meta]) => (
                      <option key={k} value={k}>{meta.label}</option>
                    ))}
                  </select>
                  <input
                    value={f.value}
                    onChange={(e) => updateFilter(i, "value", e.target.value)}
                    placeholder={t("reportBuilderHub.builder.valuePlaceholder")}
                    className="flex-1 border rounded px-2 py-1 text-xs bg-background"
                  />
                  <button onClick={() => removeFilter(i)} className="text-red-500 hover:text-red-700">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {filters.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("reportBuilderHub.builder.noFilters")}</p>
              )}
            </div>

            {/* Visualisation */}
            <div className="bg-card border rounded-xl p-4 space-y-3">
              <h3 className="font-semibold text-sm">{t("reportBuilderHub.builder.visualization")}</h3>
              <div className="grid grid-cols-2 gap-2">
                {CHART_TYPES.map((ct) => {
                  const Icon = ct.icon;
                  return (
                    <button
                      key={ct.id}
                      onClick={() => setChartType(ct.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${
                        chartType === ct.id
                          ? "bg-primary text-primary-foreground border-primary"
                          : "hover:bg-accent"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {chartTypeLabel(ct.id, t)}
                    </button>
                  );
                })}
              </div>
              {chartType !== "table" && (
                <div>
                  <label className="block text-xs font-medium mb-1">{t("reportBuilderHub.builder.groupBy")}</label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                  >
                    <option value="">- {t("reportBuilderHub.common.none")} -</option>
                    {availableCols
                      .filter(([, col]) => col.type === "string")
                      .map(([key, col]) => (
                        <option key={key} value={key}>{col.label}</option>
                      ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium mb-1">{t("reportBuilderHub.builder.rowLimit")}</label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={rowLimit}
                  onChange={(e) => setRowLimit(parseInt(e.target.value) || 100)}
                  className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handlePreview}
                disabled={previewing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-accent disabled:opacity-50"
              >
                <Play className="h-3.5 w-3.5" />
                {previewing ? t("reportBuilderHub.common.loading") : t("reportBuilderHub.builder.preview")}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !reportName || !source}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" />
                {saving ? t("reportBuilderHub.common.saving") : initialReport ? t("reportBuilderHub.common.update") : t("reportBuilderHub.common.save")}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Right panel — preview */}
      <div className="lg:col-span-2">
        {!source ? (
          <div className="h-full flex items-center justify-center border rounded-xl bg-card min-h-64">
            <div className="text-center text-muted-foreground">
              <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t("reportBuilderHub.builder.selectSource")}</p>
            </div>
          </div>
        ) : preview ? (
          <div className="space-y-4">
            <div className="bg-card border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium">{t("reportBuilderHub.builder.previewTitle", { count: preview.total, suffix: preview.total !== 1 ? "s" : "" })}</span>
                <span className="text-xs text-muted-foreground">{t("reportBuilderHub.builder.previewLimit")}</span>
              </div>
              {preview.rows?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">{t("reportBuilderHub.builder.emptyCriteria")}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        {preview.columns.map((col) => (
                          <th key={col} className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.slice(0, 20).map((row, i) => (
                        <tr key={i} className="border-b hover:bg-accent/30">
                          {preview.columns.map((col) => (
                            <td key={col} className="px-3 py-2 whitespace-nowrap">
                              {row[col] !== null && row[col] !== undefined ? String(row[col]) : "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.rows.length > 20 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      {t("reportBuilderHub.builder.extraRows", { count: preview.rows.length - 20 })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center border rounded-xl bg-card min-h-64">
            <div className="text-center text-muted-foreground">
              <Play className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t("reportBuilderHub.builder.clickPreview")}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Schedules ──────────────────────────────────────────────────────────────── */
function SchedulesTab() {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState([]);
  const [reports, setReports] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = () => {
    apiClient.get("v1/report-builder/schedules").then((r) => setSchedules(r.data)).catch(() => {});
    apiClient.get("v1/report-builder/reports").then((r) => setReports(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t("reportBuilderHub.schedules.deleteConfirm"))) return;
    await apiClient.delete(`v1/report-builder/schedules/${id}`);
    load();
  };

  const handleSave = async ({ reportId, frequency, recipients }) => {
    await apiClient.post(`v1/report-builder/reports/${reportId}/schedules`, {
      frequency,
      recipients: recipients.split(",").map((e) => e.trim()).filter(Boolean),
    });
    setShowModal(false);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowModal(true)}
          disabled={reports.length === 0}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> {t("reportBuilderHub.schedules.new")}
        </button>
      </div>

      {schedules.length === 0 ? (
        <div className="text-center py-16 border rounded-xl bg-card">
          <Clock className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-semibold mb-1">{t("reportBuilderHub.schedules.emptyTitle")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("reportBuilderHub.schedules.emptySubtitle")}
          </p>
        </div>
      ) : (
        <div className="bg-card border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                {[t("reportBuilderHub.schedules.headers.report"), t("reportBuilderHub.schedules.headers.frequency"), t("reportBuilderHub.schedules.headers.recipients"), t("reportBuilderHub.schedules.headers.nextRun"), ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.id} className="border-b hover:bg-accent/30">
                  <td className="px-4 py-3 font-medium">{s.report_name || `#${s.report_id}`}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                      {frequencyLabel(s.frequency, t)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                      {(s.recipients || []).join(", ") || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {s.next_run_at ? new Date(s.next_run_at).toLocaleString(i18n.language) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-700 p-1">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ScheduleModal reports={reports} onSave={handleSave} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

function ScheduleModal({ reports, onSave, onClose }) {
  const { t } = useTranslation();
  const [reportId, setReportId] = useState(reports[0]?.id || "");
  const [frequency, setFrequency] = useState("weekly");
  const [recipients, setRecipients] = useState("");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-xl shadow-xl w-full max-w-md">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="font-semibold">{t("reportBuilderHub.schedules.modalTitle")}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("reportBuilderHub.schedules.report")}</label>
            <select
              value={reportId}
              onChange={(e) => setReportId(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
            >
              {reports.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("reportBuilderHub.schedules.frequency")}</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
            >
              {FREQUENCIES.map((frequency) => (
                <option key={frequency} value={frequency}>{frequencyLabel(frequency, t)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("reportBuilderHub.schedules.recipients")}</label>
            <input
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="rh@company.com, manager@company.com"
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
            />
          </div>
        </div>
        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-accent">{t("reportBuilderHub.common.cancel")}</button>
          <button
            onClick={() => onSave({ reportId, frequency, recipients })}
            disabled={!reportId || !recipients}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {t("reportBuilderHub.schedules.schedule")}
          </button>
        </div>
      </div>
    </div>
  );
}
